<template>
  <div
    v-if="barVisible && noticeBarState.findNoticeDatas.length"
    class="render-notice-bar"
    :class="{ 'render-notice-bar--plain': config.withCard === false }"
    :style="barStyle"
  >
    <div class="render-notice-bar__main">
      <el-icon
        v-if="config.showIcon !== false"
        class="render-notice-bar__icon"
        :style="{ color: config.iconColor || config.textColor || '#ed6a0c' }"
      >
        <Bell />
      </el-icon>
      <span
        v-if="!isScrollMode"
        class="render-notice-bar__text"
        :style="{ color: config.textColor || '#ed6a0c' }"
        @click="openNoticeAt(0)"
      >
        {{ noticeBarState.findNoticeDatas[0]?.title || "" }}
      </span>
      <el-carousel
        v-else
        class="render-notice-bar__carousel"
        direction="vertical"
        :height="scrollViewportHeight"
        :autoplay="carouselAutoplay"
        :interval="carouselInterval"
        :loop="true"
        :show-indicators="false"
        :arrow="'never'"
        :pause-on-hover="hoverPause"
        indicator-position="none"
      >
        <el-carousel-item
          v-for="(notice, idx) in noticeBarState.findNoticeDatas"
          :key="`${notice?.id || idx}-${idx}`"
          class="render-notice-bar__carousel-item"
          @click="openNoticeAt(idx)"
        >
          <span
            class="render-notice-bar__text render-notice-bar__text--slide"
            :style="{ color: config.textColor || '#ed6a0c', fontSize: `${fontSize}px` }"
          >
            {{ notice?.title || "" }}
          </span>
        </el-carousel-item>
      </el-carousel>
    </div>

    <div class="render-notice-bar__actions">
      <NuxtLink
        v-if="config.showMore !== false && moreLink"
        class="render-notice-bar__more"
        :to="moreLink"
        :style="{ color: config.textColor || '#ed6a0c' }"
      >
        {{ getLocaleText("查看更多") }}
      </NuxtLink>
      <button
        v-if="config.showClose !== false"
        class="render-notice-bar__close"
        type="button"
        @click.stop="hideCurrentBar"
      >
        <el-icon :style="{ color: config.textColor || '#ed6a0c' }">
          <Close />
        </el-icon>
      </button>
    </div>

    <el-dialog
      v-model="noticeBarState.noticeDataVisible"
      :width="`${config.popupWidth || 820}px`"
      append-to-body
      class="render-notice-dialog"
    >
      <template #header>
        <div class="render-notice-dialog__header">
          <span class="render-notice-dialog__title">
            {{ currentNotice?.title || "" }}
          </span>
          <button
            class="render-notice-dialog__silence"
            type="button"
            @click="closeForThreeDays"
          >
            {{ $t("actions.hideNoticesForThreeDays") }}
          </button>
        </div>
      </template>

      <div
        class="render-notice-dialog__content"
        :style="{ maxHeight: `${config.popupHeight || 560}px` }"
        v-html="currentNotice?.content || ''"
      ></div>

      <template #footer>
        <div class="render-notice-dialog__footer">
          <span class="render-notice-dialog__pager">
            {{ noticeBarState.viewNoticeIdx + 1 }}/{{ noticeBarState.findNoticeDatas.length }}
          </span>
          <el-button
            v-if="noticeBarState.viewNoticeIdx > 0"
            @click="noticeBarState.viewNoticeIdx -= 1"
          >
            {{ $t("actions.查看上一条") }}
          </el-button>
          <el-button
            v-if="noticeBarState.viewNoticeIdx < noticeBarState.findNoticeDatas.length - 1"
            type="primary"
            @click="noticeBarState.viewNoticeIdx += 1"
          >
            {{ $t("actions.查看下一条") }}
          </el-button>
          <el-button @click="noticeBarState.noticeDataVisible = false">
            {{ $t("actions.关闭") }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { Bell, Close } from "@element-plus/icons-vue";
import { useGetNoticesList } from "~/user-center/src/components/NoticeBar/useGetNoticesList";
import {
  hideNoticeByDays,
  isNoticeHiddenByScope,
} from "~/user-center/src/components/NoticeBar/noticeUtils";
import useLocate from "./uses/useLocate.js";

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});
const { getLocaleText } = useLocate();

const barVisible = ref(true);
const scopeKey = computed(() => `render:${props.config?.id || "notice-bar"}`);
const { getListAction, noticeBarState } = useGetNoticesList({
  isShowBar: true,
  autoPopup: props.config?.autoPopup !== false,
  pageSize: props.config?.maxCount || 50,
  storageScope: scopeKey.value,
});

const currentNotice = computed(() => {
  return noticeBarState.findNoticeDatas[noticeBarState.viewNoticeIdx] || null;
});

const moreLink = computed(() => {
  return props.config?.moreLink || "/user-center/mall/notices";
});

const barStyle = computed(() => {
  if (props.config?.withCard === false) {
    return {};
  }
  return {
    backgroundColor: props.config?.bgColor || "#fff7e6",
  };
});

const isScrollMode = computed(() => {
  const notices = noticeBarState.findNoticeDatas || [];
  return props.config?.showStyle === "scroll" && notices.length > 1;
});

const toPositiveNumber = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return fallback;
};

const fontSize = computed(() =>
  toPositiveNumber(props.config?.fontSize, 14)
);

const carouselInterval = computed(() =>
  toPositiveNumber(props.config?.scrollInterval, 3000)
);
const hoverPause = computed(() => props.config?.scrollHoverPause !== false);
const carouselAutoplay = computed(() => isScrollMode.value);

const scrollViewportHeight = computed(() => `${fontSize.value + 12}px`);

watch(
  () => scopeKey.value,
  () => {
    barVisible.value = !isNoticeHiddenByScope(scopeKey.value);
  },
  { immediate: true }
);

onMounted(() => {
  getListAction();
});

function openNoticeAt(idx) {
  const total = noticeBarState.findNoticeDatas.length;
  if (!total) return;
  noticeBarState.viewNoticeIdx = ((idx % total) + total) % total;
  noticeBarState.noticeDataVisible = true;
}

function hideCurrentBar() {
  barVisible.value = false;
}

function closeForThreeDays() {
  hideNoticeByDays(scopeKey.value, 3);
  noticeBarState.noticeDataVisible = false;
  barVisible.value = false;
}
</script>

<style scoped lang="less">
.render-notice-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  border-radius: 12px;
  height: 100%;
}

.render-notice-bar--plain {
  padding: 0;
  gap: 10px;
  min-height: auto;
  border-radius: 0;
  background: transparent !important;
}

.render-notice-bar__main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  cursor: pointer;
}

.render-notice-bar__icon {
  font-size: 18px;
  flex: 0 0 auto;
}

.render-notice-bar__title {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.render-notice-bar__text {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  
}

.render-notice-bar__text--slide {
  display: block;
  width: 100%;
}

.render-notice-bar__carousel {
  flex: 1;
  min-width: 0;
}

.render-notice-bar__carousel :deep(.el-carousel__container) {
  height: 100%;
}

.render-notice-bar__carousel-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.render-notice-bar__carousel :deep(.el-carousel__item) {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.render-notice-bar__carousel :deep(.el-carousel__mask) {
  display: none;
}

.render-notice-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.render-notice-bar__more,
.render-notice-bar__close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  text-decoration: none;
}

.render-notice-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.render-notice-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.render-notice-dialog__silence {
  border: none;
  background: transparent;
  color: var(--el-color-primary);
  cursor: pointer;
  font-size: 13px;
}

.render-notice-dialog__content {
  overflow: auto;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}

.render-notice-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.render-notice-dialog__pager {
  margin-right: auto;
  color: #909399;
}
</style>
